import Phaser from 'phaser';
import {
    EXPLORATION_HOUSE_CENTER_X,
    LOVER_HOME_X,
    MOVE_INTERVAL,
    TILE_SIZE,
    RoomBox,
} from '@blockworld/shared';
import { calculateExplorationHouseSurfaceY } from '@blockworld/shared';
import { SpeechBubble } from '../ui/SpeechBubble';
import { TokiPonaAudio, VOICE_PROFILES } from '../toki/TokiPonaAudio';

// ============================================================
// LOVER NPC BEHAVIOR
// "jan olin" — follows one player at a time inside the exploration house.
// Says "toki" on entry, walks toward the player at 1 tile per 100ms,
// stops 1 tile away. Swaps positions if the player steps onto her tile.
// Returns to center when the room empties.
// ============================================================

const LOVER_VOICE_KEY = 'lili';
const GREETING_WORDS = [108]; // "toki"
const GREETING_COOLDOWN_MS = 5000;
const STEP_MS = MOVE_INTERVAL; // 100ms per tile
const STATIONARY_FACE_MS = 300; // player auto-faces Lover after 300ms stationary

// Interior walk bounds (inside the LOG entry columns)
const EH_X1 = EXPLORATION_HOUSE_CENTER_X - 4; // 12
const EH_X2 = EXPLORATION_HOUSE_CENTER_X + 4; // 20

type LoverState = 'idle' | 'greeting' | 'following' | 'returning';

export class LoverBehavior {
    private scene: Phaser.Scene;
    private audio: TokiPonaAudio;
    private worldSeed: number;

    /** Current world tile X (mutable — she walks). */
    x: number;
    /** Current world tile Y (head tile — always on the platform). */
    y: number;
    /** Current facing: 1 = right, -1 = left. */
    facing = 1;

    private homeX: number;
    private state: LoverState = 'idle';
    private lastMoveTime = 0;

    // Tracking
    private wasLocalInRoom = false;
    private lastTrackedX = 0; // previous tick's tracked player X
    private lastPlayerDir = 1; // direction the last player left from

    // Player stationary detection (for auto-facing toward Lover)
    private playerStationaryX = 0;
    private playerStationarySince = 0;

    // Speech
    private activeBubble: SpeechBubble | null = null;
    private lastGreetingTime = 0;

    /** Exploration house is the 3rd room box (index 2). */
    private static ROOM_BOX_INDEX = 2;

    constructor(scene: Phaser.Scene, audio: TokiPonaAudio, seed: number) {
        this.scene = scene;
        this.audio = audio;
        this.worldSeed = seed;

        this.homeX = LOVER_HOME_X;
        const platformY = calculateExplorationHouseSurfaceY(seed);
        this.x = this.homeX;
        this.y = platformY - 2; // head tile (feet at platformY-1, standing on platform)
    }

    /**
     * Call each frame. Handles entry detection, following, returning, greeting.
     * `otherPlayerPositions` should provide current (x, y) for each remote player.
     */
    update(
        now: number,
        localPlayerX: number,
        localPlayerY: number,
        roomBoxes: RoomBox[],
        otherPlayerPositions: Map<string, { x: number; y: number }>,
        offsetX: number,
        offsetY: number,
    ): void {
        const box = roomBoxes[LoverBehavior.ROOM_BOX_INDEX];
        if (!box) return;

        const localInRoom = this.isInBox(localPlayerX, localPlayerY, box);
        const justEntered = localInRoom && !this.wasLocalInRoom;
        this.wasLocalInRoom = localInRoom;

        // On entry: greet (if cooldown allows) and start following
        if (justEntered && (this.state === 'idle' || this.state === 'returning')) {
            this.facing = localPlayerX < this.x ? -1 : 1;
            if (now - this.lastGreetingTime >= GREETING_COOLDOWN_MS) {
                this.state = 'greeting';
                this.lastGreetingTime = now;
                this.createBubble(GREETING_WORDS, offsetX, offsetY);
            } else {
                this.state = 'following';
            }
        }

        switch (this.state) {
            case 'greeting':
                // Move toward player even while greeting
                if (localInRoom) {
                    this.stepToward(now, localPlayerX);
                }
                if (this.activeBubble && !this.activeBubble.isActive()) {
                    this.activeBubble = null;
                    if (localInRoom) {
                        this.state = 'following';
                    } else if (this.findOtherInRoom(otherPlayerPositions, box) !== null) {
                        this.state = 'following';
                    } else {
                        this.state = 'returning';
                    }
                }
                break;

            case 'idle':
                // Nothing to do until a player enters
                break;

            case 'following': {
                // Determine who to follow
                let targetX: number;
                let haveTarget: boolean;

                if (localInRoom) {
                    targetX = localPlayerX;
                    haveTarget = true;
                } else {
                    // Local player left — remember which direction
                    this.lastPlayerDir = localPlayerX > this.x ? 1 : -1;

                    // Check for any other player in the room
                    const other = this.findOtherInRoom(otherPlayerPositions, box);
                    if (other !== null) {
                        targetX = other;
                        haveTarget = true;
                    } else {
                        // Nobody in room — start returning
                        this.state = 'returning';
                        break;
                    }
                }

                const targetTileX = Math.floor(targetX);

                // Swap: player stepped onto Lover's tile
                if (targetTileX === this.x && haveTarget) {
                    // Move to where the player was last tick
                    const swapX = this.clampInterior(this.lastTrackedX);
                    if (swapX !== this.x) {
                        this.x = swapX;
                        this.facing = targetTileX > this.x ? 1 : targetTileX < this.x ? -1 : this.facing;
                    }
                } else {
                    this.stepToward(now, targetX);
                }

                this.lastTrackedX = targetTileX;
                break;
            }

            case 'returning':
                // If a player re-enters, switch to greeting/following
                if (localInRoom) {
                    this.facing = localPlayerX < this.x ? -1 : 1;
                    if (now - this.lastGreetingTime >= GREETING_COOLDOWN_MS) {
                        this.state = 'greeting';
                        this.lastGreetingTime = now;
                        this.createBubble(GREETING_WORDS, offsetX, offsetY);
                    } else {
                        this.state = 'following';
                    }
                    break;
                }

                // Check for other players
                const otherX = this.findOtherInRoom(otherPlayerPositions, box);
                if (otherX !== null) {
                    this.state = 'following';
                    this.lastTrackedX = Math.floor(otherX);
                    break;
                }

                // Move toward home
                if (this.x !== this.homeX) {
                    this.stepToward(now, this.homeX, 0);
                } else {
                    // Arrived home — face where the player left from
                    this.facing = this.lastPlayerDir;
                    this.state = 'idle';
                }
                break;
        }

        // Track how long the local player has been stationary (for auto-facing)
        const playerTileX = Math.floor(localPlayerX);
        if (playerTileX !== this.playerStationaryX) {
            this.playerStationaryX = playerTileX;
            this.playerStationarySince = now;
        }
    }

    /**
     * Returns the facing direction the player should adopt (-1 or 1) if they've
     * been stationary for 300ms while the Lover is tracking them, or null otherwise.
     */
    getPlayerFacingOverride(localPlayerX: number, localPlayerY: number, now: number, roomBoxes: RoomBox[]): number | null {
        const box = roomBoxes[LoverBehavior.ROOM_BOX_INDEX];
        if (!box) return null;

        // Only applies when Lover is actively tracking the local player
        if (this.state !== 'following' && this.state !== 'greeting') return null;
        if (!this.isInBox(localPlayerX, localPlayerY, box)) return null;

        // Check stationary duration
        if (now - this.playerStationarySince < STATIONARY_FACE_MS) return null;

        // Face toward the Lover
        const playerTileX = Math.floor(localPlayerX);
        if (this.x > playerTileX) return 1;   // Lover is to the right
        if (this.x < playerTileX) return -1;  // Lover is to the left
        return null; // Same tile — no override
    }

    /**
     * Move 1 tile toward targetX if enough time has elapsed.
     * Stops when within `stopDist` tiles (default 1 for following, 0 for returning).
     */
    private stepToward(now: number, targetX: number, stopDist: number = 1): void {
        if (now - this.lastMoveTime < STEP_MS) return;

        const targetTileX = Math.floor(targetX);
        const dx = targetTileX - this.x;

        if (Math.abs(dx) <= stopDist) {
            // Close enough — just face the target
            if (dx !== 0) this.facing = dx > 0 ? 1 : -1;
            return;
        }

        const step = dx > 0 ? 1 : -1;
        const newX = this.clampInterior(this.x + step);
        if (newX !== this.x) {
            this.x = newX;
            this.facing = step;
            this.lastMoveTime = now;
        }
    }

    /** Clamp X to the walkable interior (inside LOG columns). */
    private clampInterior(x: number): number {
        return Math.max(EH_X1, Math.min(EH_X2, x));
    }

    /** Find the X of the first other player in the room box, or null. */
    private findOtherInRoom(
        others: Map<string, { x: number; y: number }>,
        box: RoomBox,
    ): number | null {
        for (const [, pos] of others) {
            if (this.isInBox(pos.x, pos.y, box)) {
                return pos.x;
            }
        }
        return null;
    }

    private isInBox(x: number, y: number, box: RoomBox): boolean {
        return x >= box.x1 && x <= box.x2 && y >= box.y1 && y <= box.y2;
    }

    /** Return the active speech bubble for constellation overlay integration. */
    getActiveBubble(): SpeechBubble | null {
        return this.activeBubble && this.activeBubble.isActive() ? this.activeBubble : null;
    }

    /** Update the speech bubble screen position (call during render). */
    updateBubblePosition(offsetX: number, offsetY: number): void {
        if (this.activeBubble && this.activeBubble.isActive()) {
            const screenX = this.x * TILE_SIZE + offsetX;
            const screenY = this.y * TILE_SIZE + offsetY;
            this.activeBubble.updatePosition(screenX, screenY);
        }
    }

    destroy(): void {
        if (this.activeBubble) {
            this.activeBubble.destroy();
            this.activeBubble = null;
        }
        this.state = 'idle';
        this.wasLocalInRoom = false;
    }

    private createBubble(words: number[], offsetX: number, offsetY: number): void {
        if (this.activeBubble && this.activeBubble.isActive()) {
            this.activeBubble.destroy();
        }
        const screenX = this.x * TILE_SIZE + offsetX;
        const screenY = this.y * TILE_SIZE + offsetY;

        this.activeBubble = new SpeechBubble({
            scene: this.scene,
            words,
            playerScreenX: screenX,
            playerScreenY: screenY,
            audio: this.audio,
            profile: VOICE_PROFILES[LOVER_VOICE_KEY],
        });
    }
}
